/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useDispatch, useSelector } from 'react-redux';
import { Box, FormControlLabel, FormLabel, Radio, RadioGroup } from '@mui/material';
import {
  selectFavoriteScenarios,
  selectScenarios,
  selectSearchData,
  selectSelectedScenario,
} from '../../selectors';
import { searchActions } from '../../slice';

/**
 * Date range picker used to filter stay dates in search searchScenarios.
 *
 * @param {Object} filterData
 * @returns {JSX.Element}
 */
const SearchComparisonAlignment = ({ filterData, handleClose, found }) => {
  const dispatch = useDispatch();
  const scenarios = useSelector(selectScenarios);
  const searchScenarios = useSelector(selectSearchData);
  const favoriteScenarios = useSelector(selectFavoriteScenarios);
  const selectedScenario = useSelector(selectSelectedScenario);

  // Handle comparison alignment and update searchScenarios in the store
  const handleChange = (event, value) => {
    event.stopPropagation();
    const keyword = value === 'true' ? 'aligned' : 'not aligned';
    //
    if (scenarios?.results.length) {
      const updatedScenarios = scenarios?.results.map((scenario) => {
        if (scenario.id !== filterData?.scenarioId) return scenario;
        //
        return {
          ...scenario,
          description: scenario.description.replace(filterData.keyword, keyword),
          visuals: scenario.visuals.map((visual) => {
            const updatedFilters = Object.entries(visual.filters).reduce(
              (acc, [filterName, filter]) => {
                if (filter?.keyword === found?.keyword) {
                  acc[filterName] = {
                    ...filter,
                    keyword,
                    value: value === 'true',
                  };
                } else {
                  acc[filterName] = filter;
                }
                return acc;
              },
              {}
            );

            return {
              ...visual,
              filters: updatedFilters,
            };
          }),
        };
      });

      if (selectedScenario) {
        const updatedScenario = updatedScenarios?.find((e) => e.id === filterData?.scenarioId);
        dispatch(searchActions.setSelectedScenario(updatedScenario));
      }
      dispatch(searchActions.setScenarios({ results: updatedScenarios }));
    }
    //
    if (favoriteScenarios.length) {
      const updatedScenarios = favoriteScenarios.map((scenario) => {
        if (scenario.id !== filterData?.scenarioId) return scenario;
        //
        return {
          ...scenario,
          description: scenario.description.replace(filterData.keyword, keyword),
          visuals: scenario.visuals.map((visual) => {
            const updatedFilters = Object.entries(visual.filters).reduce(
              (acc, [filterName, filter]) => {
                if (filter?.keyword === found?.keyword) {
                  acc[filterName] = {
                    ...filter,
                    keyword,
                    value: value === 'true',
                  };
                } else {
                  acc[filterName] = filter;
                }
                return acc;
              },
              {}
            );
            return {
              ...visual,
              filters: updatedFilters,
            };
          }),
        };
      });
      if (selectedScenario) {
        const updatedScenario = updatedScenarios?.find((e) => e.id === filterData?.scenarioId);
        dispatch(searchActions.setSelectedScenario(updatedScenario));
      }
      dispatch(searchActions.setFavoriteScenario(updatedScenarios));
    }
    //
    if (searchScenarios.length) {
      const updatedScenarios = searchScenarios.map((scenario) => {
        if (scenario.id !== filterData?.scenarioId) return scenario;
        //
        return {
          ...scenario,
          description: scenario.description.replace(filterData.keyword, keyword),
          visuals: scenario.visuals.map((visual) => {
            const updatedFilters = Object.entries(visual.filters).reduce(
              (acc, [filterName, filter]) => {
                if (filter?.keyword === found?.keyword) {
                  acc[filterName] = {
                    ...filter,
                    keyword,
                    value: value === 'true',
                  };
                } else {
                  acc[filterName] = filter;
                }
                return acc;
              },
              {}
            );
            return {
              ...visual,
              filters: updatedFilters,
            };
          }),
        };
      });
      if (selectedScenario) {
        const updatedScenario = updatedScenarios?.find((e) => e.id === filterData?.scenarioId);
        dispatch(searchActions.setSelectedScenario(updatedScenario));
      }
      dispatch(searchActions.setSearchScenario(updatedScenarios));
    }
    handleClose();
  };
  //
  return (
    <Box sx={{ p: 4, backgroundColor: '#ffffff' }}>
      <FormLabel>Alignment</FormLabel>

      <RadioGroup
        row
        value={String(found.value)} // "true" | "false"
        onChange={handleChange}
      >
        <FormControlLabel value="true" control={<Radio />} label="Enable" />
        <FormControlLabel value="false" control={<Radio />} label="Disable" />
      </RadioGroup>
    </Box>
  );
};

export default SearchComparisonAlignment;
